<template>
	<ul class="tree" :class="{ 'indent-tree': indent }">
		<li v-for="(node, index) in nodes" :key="index">
			<div class="node">
				<span v-if="node.children" class="toggle-icon" @click="toggleNode(node)">
					{{ isNodeExpanded(node.key) ? "▼" : "▶" }}
				</span>
				<span v-else class="toggle-icon" />
				<input
					type="checkbox"
					:checked="isChecked(node)"
					:indeterminate="isIndeterminate(node)"
					@change="handleCheckboxChange(node, $event)"
				/>
				{{ node.label }}
			</div>
			<FeatureTree
				v-if="node.children && isNodeExpanded(node.key)"
				:nodes="node.children"
				:indent="true"
			/>
		</li>
	</ul>
</template>

<script setup lang="ts">
import { EditorMode, LoadingState } from "@/model/Enums";
import { FeatureType } from "@/model/FeatureType";
import { IFeature } from "@/model/IFeature";
import { useMainStore } from "@/store/mainStore";

export interface TreeNode {
	label: string;
	key: string;
	featureId?: number;
	children?: TreeNode[];
}

defineProps<{ nodes: TreeNode[]; indent: boolean }>();

const store = useMainStore();

const isFeatureSelected = (featureId: number, key: string) => {
	if (key.startsWith("weldline")) {
		return store.weldlines.some((sw) => sw.weldlineId === featureId && sw.selected);
	}
	return store.selectedFeatures.some((feature: IFeature) => feature.featureId === featureId);
};
const isNodeExpanded = (key: string) => {
	return store.expandedFeatures.includes(key);
};

const toggleNode = (node: TreeNode) => {
	if (node.children) {
		store.addOrRemoveExpandedFeature(node.key);
	}
};

function isChecked(node: TreeNode): boolean {
	if (node.featureId) {
		// This is a feature, not a grouping
		// So the check is solely determined by the presence in the store.selectedFeatures
		return isFeatureSelected(node.featureId, node.key);
	}
	// If this is reached, this node is not a feature, but only a grouping
	if (node.children) {
		const allChecked = node.children.every((child) => isChecked(child));
		return allChecked;
	}
	console.error("This should not be reached in 'isChecked'", node);
	return false;
}
function isIndeterminate(node: TreeNode): boolean {
	if (node.featureId) {
		// This is a feature, not a grouping
		// A feature can only ever be checked or unckecked. It can't be indeterminate.
		return false;
	}
	// If this is reached, this node is not a feature, but only a grouping
	if (node.children) {
		const allChecked = node.children.every((child) => isChecked(child));
		const someChecked = node.children.some(
			(child) => isChecked(child) || isIndeterminate(child)
		);
		// It's only indeterminate if not all the children are checked
		// AND some of the children are checked or indeterminate
		return !allChecked && someChecked;
	}
	console.error("This should not be reached in 'isIndeterminate'", node);
	return false;
}

const handleCheckboxChange = (node: TreeNode, event: Event) => {
	console.log(node);
	const isWeldlineNode =
		node.key.startsWith("weldline") || node.key === "Group " + FeatureType[FeatureType.Weldline];
	if (
		!store.article.isAssembly &&
		store.editorMode !== EditorMode.FeatureMode &&
		!isWeldlineNode
	) {
		store.setEditorMode(EditorMode.FeatureMode);
	} else if (
		!store.article.isAssembly &&
		store.editorMode !== EditorMode.WeldMode &&
		isWeldlineNode
	) {
		store.setEditorMode(EditorMode.WeldMode);
	}
	const target = event.target as HTMLInputElement;

	// If there are more than 50 features to be toggled,
	// start the loading animation before doing so. It could take a while.
	const childCount = getChildrenCount(node);
	if (childCount > 50) {
		store.setLoadingState(LoadingState.MarkFeatures);

		// Defer execution of the heavy logic to allow UI update
		setTimeout(() => {
			processCheckboxChange(node, target.checked);
			store.setLoadingState(LoadingState.Done);
		}, 0);
	} else {
		processCheckboxChange(node, target.checked);
	}
};
const processCheckboxChange = (node: TreeNode, checked: boolean) => {
	const allSelected: number[] = [];
	if (node.featureId && node.key.startsWith("weldline")) {
		// for weldlines, toggle the show flag on the weldlines themselves
		store.weldlines.find((wl) => wl.weldlineId === node.featureId)!.selected = checked;
	} else if (node.featureId) {
		// add or remove from selected features if this is a feature
		// featureId is only set if this is a feature and not a group
		allSelected.push(node.featureId);
	}
	findAllChildFeatureIds(node, checked, allSelected);
	store.addOrRemoveSelectedFeatures(allSelected, checked);
};

const getChildrenCount = (node: TreeNode): number => {
	if (!node.children || node.children.length === 0) {
		return 0;
	}
	return (
		node.children.length +
		node.children.reduce((sum, child) => sum + getChildrenCount(child), 0)
	);
};

// Recursively set checked state for children
const findAllChildFeatureIds = (node: TreeNode, checked: boolean, allSelected: number[]) => {
	if (node.children) {
		node.children.forEach((child) => {
			if (child.featureId && child.key.startsWith("weldline")) {
				store.weldlines.find((wl) => wl.weldlineId === child.featureId)!.selected = checked;
			} else if (child.featureId) {
				allSelected.push(child.featureId);
			}
			findAllChildFeatureIds(child, checked, allSelected);
		});
	}
};
</script>

<style scoped>
.tree {
	list-style: none;
}
.indent-tree {
	padding-left: 1rem;
}
.node {
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 6px;
}
.toggle-icon {
	display: inline-block;
	width: 14px;
	font-size: 14px;
}
</style>
